// Top-right country/currency selector — pill trigger opens a searchable modal.
// Mobile-first: full-screen sheet on phones, centered card on desktop.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Globe, Search, X, CheckCircle2, History } from 'lucide-react';
import { useCurrency } from '../../context/CurrencyContext';
import { POPULAR_COUNTRY_CODES } from '../../lib/countries';

// Small <Flag/> helper — renders the country flag as a CDN image (always visible,
// unlike Unicode regional-indicator emoji which fall back to letters on Windows/Linux).
const Flag = ({ code, alt, className = '' }) => (
  <img
    src={`https://flagcdn.com/h20/${(code || '').toLowerCase()}.png`}
    srcSet={`https://flagcdn.com/h40/${(code || '').toLowerCase()}.png 2x`}
    alt={alt || code}
    loading="lazy"
    className={`inline-block rounded-sm object-cover ${className}`}
    onError={(e) => { e.currentTarget.style.display = 'none'; }}
  />
);

const Row = ({ c, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`country-row-${c.country_code}`}
    className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-colors tap-target ${selected ? 'bg-red-50 dark:bg-red-950/30 ring-1 ring-red-500/30' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}
  >
    <Flag code={c.country_code} alt={c.country_name} className="h-5 w-7 shrink-0 shadow-sm" />
    <div className="min-w-0 flex-1">
      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{c.country_name}</p>
      <p className="text-[11px] text-gray-500">{c.currency_code} · {c.currency_symbol}</p>
    </div>
    {selected && <CheckCircle2 className="h-4 w-4 text-red-600" />}
  </button>
);

const CountryCurrencyPicker = ({ compact = false }) => {
  const { country, countries, recent, setCountry } = useCurrency();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    document.body.style.overflow = 'hidden';
    setQuery('');
    const t = setTimeout(() => inputRef.current?.focus(), 80);
    return () => { document.body.style.overflow = ''; clearTimeout(t); };
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return countries;
    return countries.filter((c) =>
      c.country_name.toLowerCase().includes(q)
      || c.country_code.toLowerCase().includes(q)
      || c.currency_code.toLowerCase().includes(q)
      || c.currency_symbol.toLowerCase().includes(q)
    );
  }, [q, countries]);
  const popular = useMemo(() => countries.filter((c) => POPULAR_COUNTRY_CODES.includes(c.country_code)), [countries]);

  const onSelect = (cc) => { setCountry(cc); setOpen(false); };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="country-currency-trigger"
        title={`${country.country_name} · ${country.currency_code} (${country.currency_symbol})`}
        className={`inline-flex items-center gap-2 rounded-full border transition-colors tap-target px-3 h-9 ${
          compact
            ? 'bg-white/15 backdrop-blur-md border-white/20 text-white hover:bg-white/25'
            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800'
        }`}
      >
        <Flag code={country.country_code} alt={country.country_name} className="h-3.5 w-5 shadow-sm" />
        <span className={`hidden sm:inline text-xs font-bold ${compact ? 'text-white' : 'text-gray-800 dark:text-gray-200'}`}>
          {country.currency_code} ({country.currency_symbol})
        </span>
        <Globe className={`hidden md:block h-3.5 w-3.5 ${compact ? 'text-white/70' : 'text-gray-400'}`} />
      </button>

      {open && (
        <div className="fixed inset-0 z-[80] flex items-stretch sm:items-center justify-center" data-testid="country-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative bg-white dark:bg-gray-900 w-full sm:max-w-md sm:rounded-2xl sm:shadow-2xl max-h-[100vh] sm:max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-4 sm:px-5 h-14 border-b border-gray-200 dark:border-gray-800">
              <h2 className="font-bold text-gray-900 dark:text-white flex items-center gap-2"><Globe className="h-5 w-5 text-red-600" /> Choose country & currency</h2>
              <button onClick={() => setOpen(false)} aria-label="Close" data-testid="country-modal-close"
                      className="tap-target p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
                <X className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            <div className="px-4 sm:px-5 py-3 border-b border-gray-200 dark:border-gray-800 sticky top-0 bg-white dark:bg-gray-900 z-10">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search country or currency…"
                  data-testid="country-search"
                  className="w-full h-11 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 pl-9 pr-3 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:outline-none"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-3 sm:px-4 py-3 space-y-4">
              {!q && recent.length > 0 && (
                <section>
                  <p className="text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-1.5 px-1 flex items-center gap-1.5">
                    <History className="h-3 w-3" /> Recent
                  </p>
                  {recent.map((c) => (
                    <Row key={`r-${c.country_code}`} c={c} selected={country.country_code === c.country_code} onClick={() => onSelect(c.country_code)} />
                  ))}
                </section>
              )}

              {!q && (
                <section>
                  <p className="text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-1.5 px-1">Popular</p>
                  {popular.map((c) => (
                    <Row key={`p-${c.country_code}`} c={c} selected={country.country_code === c.country_code} onClick={() => onSelect(c.country_code)} />
                  ))}
                </section>
              )}

              <section>
                <p className="text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-1.5 px-1">{q ? `Results (${filtered.length})` : 'All countries'}</p>
                {filtered.length === 0 ? (
                  <p className="text-sm text-gray-500 px-2 py-6 text-center">No matches for “{query}”.</p>
                ) : (
                  filtered.map((c) => (
                    <Row key={c.country_code} c={c} selected={country.country_code === c.country_code} onClick={() => onSelect(c.country_code)} />
                  ))
                )}
              </section>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CountryCurrencyPicker;
